import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Space, Typography, Tag, Button, Descriptions, Tooltip, Alert } from 'antd';
import { normalizeBase } from '../utils';

const { Title, Text } = Typography;

type Indicator = { status?: string; latencyMs?: number; reason?: string };
type Queue = { name?: string; wait?: number; active?: number; delayed?: number };
type HealthJson = {
  status?: string;
  details?: {
    db?: Indicator;
    redis?: (Indicator & { queue?: Queue }) | undefined;
    env?: { status?: string } | undefined;
    secrets?: { status?: string; length?: number } | undefined;
    geoip?: Indicator;
    sendgrid?: Indicator;
    twilio?: Indicator;
  };
};

function latencyTag(ms?: number) {
  if (typeof ms !== 'number') return <Tag>n/a</Tag>;
  if (ms < 50) return <Tag color="green">{ms} ms</Tag>;
  if (ms < 150) return <Tag color="gold">{ms} ms</Tag>;
  return <Tag color="red">{ms} ms</Tag>;
}

export default function FullHealthDiagnosticsPanel({ apiBase }: { apiBase: string }) {
  const [data, setData] = useState<HealthJson | null>(null);
  const [rawText, setRawText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [httpStatus, setHttpStatus] = useState<number | null>(null);
  const [enabled, setEnabled] = useState<boolean>(false);

  const run = useCallback(async () => {
    setError(null);
    setData(null);
    setRawText('');
    setHttpStatus(null);
    if (apiBase == null) return;
    try {
      setLoading(true);
      const base = normalizeBase(apiBase);
      const url = `${base}/api/health`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      setHttpStatus(res.status);
      const text = await res.text();
      setRawText(text);
      try {
        setData(JSON.parse(text) as HealthJson);
      } catch {
        setData(null);
      }
      // Do not throw on non-200; diagnostics may intentionally be 503
    } catch (e) {
      setError((e as Error)?.message || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (enabled) void run();
  }, [run, enabled]);

  const copyJson = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(rawText || '');
    } catch {
      // ignore
    }
  };

  const statusTag = useMemo(() => {
    const s = data?.status;
    return s ? <Tag color={s === 'ok' ? 'green' : 'red'}>{s}</Tag> : null;
  }, [data]);

  const external = useMemo(() => {
    const d = data?.details ?? {};
    const list: Array<{ key: string; label: string; ind?: Indicator }> = [
      { key: 'geoip', label: 'GeoIP', ind: (d as Record<string, Indicator>)['geoip'] },
      { key: 'sendgrid', label: 'SendGrid', ind: (d as Record<string, Indicator>)['sendgrid'] },
      { key: 'twilio', label: 'Twilio', ind: (d as Record<string, Indicator>)['twilio'] },
    ];
    return list.filter((x) => x.ind !== undefined);
  }, [data]);

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            Full Health (Diagnostics)
          </Title>
          {statusTag}
          {httpStatus !== null ? (
            <Tooltip title="HTTP status of /api/health">
              <Tag color={httpStatus === 200 ? 'green' : httpStatus >= 500 ? 'red' : 'gold'}>
                HTTP {httpStatus}
              </Tag>
            </Tooltip>
          ) : null}
        </Space>
      }
      extra={
        <Space>
          <Space>
            <Text type="secondary">Diagnostics</Text>
            <Tooltip title="Fetch /api/health on demand. This endpoint may return 503 when any external provider is down (expected in some envs).">
              <span>
                <Button
                  type={enabled ? 'primary' : 'default'}
                  onClick={() => setEnabled((v) => !v)}
                >
                  {enabled ? 'On' : 'Off'}
                </Button>
              </span>
            </Tooltip>
          </Space>
          <Button onClick={() => run()} loading={loading}>
            Refresh
          </Button>
          {apiBase != null ? (
            <Button
              href={`${normalizeBase(apiBase)}/api/health`}
              target="_blank"
              rel="noreferrer noopener"
            >
              Open
            </Button>
          ) : null}
          <Button onClick={() => void copyJson()}>Copy JSON</Button>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      {apiBase == null && <Text type="secondary">API base not configured.</Text>}
      <Alert
        type="info"
        title="Diagnostics 503 semantics"
        description="The Full Health endpoint returns 503 when any external provider indicator is down. This is expected in environments without egress or where providers are unconfigured. Use the Ready panel for k8s gating."
        showIcon
        style={{ marginBottom: 8 }}
      />
      {error && <Alert type="warning" title={error} style={{ marginBottom: 8 }} />}

      <Space orientation="vertical" style={{ width: '100%' }}>
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Database">
            <Space>
              <Text>status:</Text>
              <Tag color={data?.details?.db?.status === 'up' ? 'green' : 'red'}>
                {data?.details?.db?.status ?? 'n/a'}
              </Tag>
              <Text>latency:</Text>
              {latencyTag(data?.details?.db?.latencyMs)}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Redis">
            <Space size="small" wrap>
              <Text>status:</Text>
              <Tag color={data?.details?.redis?.status === 'up' ? 'green' : 'red'}>
                {data?.details?.redis?.status ?? 'n/a'}
              </Tag>
              <Text>latency:</Text>
              {latencyTag(data?.details?.redis?.latencyMs)}
              {data?.details?.redis?.queue?.name ? (
                <Tooltip title="Queue wait/active/delayed">
                  <Tag color="blue">
                    {data?.details?.redis?.queue?.name} · w{data?.details?.redis?.queue?.wait ?? 0}
                    /a{data?.details?.redis?.queue?.active ?? 0}/d
                    {data?.details?.redis?.queue?.delayed ?? 0}
                  </Tag>
                </Tooltip>
              ) : null}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Env">
            <Space>
              <Tag color={data?.details?.env?.status === 'up' ? 'green' : 'red'}>
                {data?.details?.env?.status ?? 'n/a'}
              </Tag>
              <Text type="secondary">(see Ready panel for counts)</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Secrets">
            <Space>
              <Tag color={data?.details?.secrets?.status === 'up' ? 'green' : 'red'}>
                {data?.details?.secrets?.status ?? 'n/a'}
              </Tag>
              {typeof data?.details?.secrets?.length === 'number' ? (
                <Text type="secondary">length {data?.details?.secrets?.length}</Text>
              ) : null}
            </Space>
          </Descriptions.Item>
        </Descriptions>

        {external.length > 0 && (
          <Card size="small" title="External Providers" style={{ marginTop: 12 }}>
            <Space orientation="vertical" style={{ width: '100%' }}>
              {external.map(({ key, label, ind }) => (
                <Space key={key}>
                  <Text>{label}</Text>
                  <Tag color={ind?.status === 'up' ? 'green' : 'red'}>{ind?.status ?? 'n/a'}</Tag>
                  {typeof ind?.latencyMs === 'number' ? latencyTag(ind?.latencyMs) : null}
                  {ind?.reason ? <Text type="secondary">{ind.reason}</Text> : null}
                </Space>
              ))}
              {external.length === 0 && (
                <Text type="secondary">No external indicators present.</Text>
              )}
            </Space>
          </Card>
        )}
      </Space>
    </Card>
  );
}
